import { useState, useEffect, useRef } from 'react';
import { X, Monitor, Search, Clock, LogOut, Loader2 } from 'lucide-react';
import { formatRemaining, isOverdue } from '@/lib/sessionTime';

export interface ActiveSession {
  id: string;
  userName: string;
  userEmail: string;
  equipment: string;
  startTime: Date;
  /** When the API will end the session on its own; null = no time limit. */
  expiresAt?: Date | null;
}

interface ActiveSessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessions: ActiveSession[];
  onEndSession?: (sessionId: string) => Promise<void>;
}

function formatDuration(start: Date): string {
  const secs = Math.floor((Date.now() - start.getTime()) / 1000);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function ActiveSessionsModal({ isOpen, onClose, sessions, onEndSession }: ActiveSessionsModalProps) {
  const [filter, setFilter] = useState('');
  const [, forceRender] = useState(0);
  // ID da sessão a aguardar confirmação de término (null = sem confirmação aberta)
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  // Ref para colocar o foco automático no botão "Não"
  const noBtnRef = useRef<HTMLButtonElement>(null);

  // Tick every second to update duration
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => forceRender((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  // Reset filter and confirmation on open/close
  useEffect(() => {
    if (isOpen) {
      setFilter('');
      setConfirmId(null);
    }
  }, [isOpen]);

  // Foco no botão "Não" quando a confirmação abre
  useEffect(() => {
    if (confirmId !== null) {
      // pequeno delay para o DOM renderizar o botão
      setTimeout(() => noBtnRef.current?.focus(), 50);
    }
  }, [confirmId]);

  if (!isOpen) return null;

  const filteredSessions = sessions.filter((s) => {
    const q = filter.toLowerCase();
    return (
      !q ||
      s.userName.toLowerCase().includes(q) ||
      s.equipment.toLowerCase().includes(q)
    );
  });

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Não fechar o modal principal quando a confirmação está aberta
    if (confirmId !== null) return;
    if (e.target === e.currentTarget) onClose();
  };

  async function handleConfirmEnd() {
    if (!confirmId || !onEndSession) return;
    setEnding(true);
    try {
      await onEndSession(confirmId);
    } finally {
      setEnding(false);
      setConfirmId(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sessions-modal-title"
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-primary" />
            <h2 id="sessions-modal-title" className="text-lg font-semibold text-foreground">
              Utilizadores em Equipamento
            </h2>
            <span className="ml-2 bg-primary/10 text-primary text-xs font-bold px-2 py-0.5 rounded-full">
              {sessions.length} activos
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter */}
        <div className="px-6 py-3 border-b border-border shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              className="input pl-9"
              placeholder="Filtrar por utilizador ou equipamento..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {filteredSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Monitor className="w-10 h-10 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">
                {filter ? 'Nenhuma sessão corresponde ao filtro.' : 'Nenhuma sessão activa.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="sticky top-0 bg-card border-b border-border">
                <tr>
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">Utilizador</th>
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">Equipamento</th>
                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">Início</th>
                  <th className="text-right px-6 py-3 font-medium text-muted-foreground">
                    <span className="flex items-center justify-end gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      Duração
                    </span>
                  </th>
                  <th className="text-right px-6 py-3 font-medium text-muted-foreground">Tempo previsto</th>
                  {onEndSession && (
                    <th className="text-right px-6 py-3 font-medium text-muted-foreground">Acção</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredSessions.map((s) => (
                  <tr key={s.id} className="hover:bg-accent/50 transition-colors">
                    <td className="px-6 py-3">
                      <div className="font-medium text-foreground">{s.userName}</div>
                      <div className="text-xs text-muted-foreground">{s.userEmail}</div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-foreground">{s.equipment}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-muted-foreground tabular-nums">
                      {s.startTime.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-6 py-3 text-right tabular-nums font-medium text-foreground">
                      {formatDuration(s.startTime)}
                    </td>
                    <td
                      className={`px-6 py-3 text-right tabular-nums ${
                        isOverdue(s.expiresAt) ? 'text-destructive font-medium' : 'text-muted-foreground'
                      }`}
                    >
                      {formatRemaining(s.expiresAt)}
                    </td>
                    {onEndSession && (
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => setConfirmId(s.id)}
                          title="Terminar sessão"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-destructive border border-destructive/30 hover:bg-destructive/10 transition-colors"
                        >
                          <LogOut className="w-3.5 h-3.5" />
                          Terminar
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border shrink-0 flex justify-end">
          <button onClick={onClose} className="btn-outline text-sm">
            Fechar
          </button>
        </div>
      </div>

      {/* ── Confirmação de término de sessão ── */}
      {confirmId !== null && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
                <LogOut className="w-5 h-5 text-destructive" />
              </div>
              <h3 className="text-base font-semibold text-foreground">Terminar Sessão</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Quer terminar a sessão?
            </p>
            <div className="flex justify-end gap-3">
              <button
                ref={noBtnRef}
                onClick={() => setConfirmId(null)}
                disabled={ending}
                className="btn-ghost disabled:opacity-50"
              >
                Não
              </button>
              <button
                onClick={handleConfirmEnd}
                disabled={ending}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive hover:bg-destructive/90 text-destructive-foreground text-sm font-medium transition-colors disabled:opacity-50"
              >
                {ending && <Loader2 className="w-4 h-4 animate-spin" />}
                Sim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
